import { ref } from 'vue';
import * as signalR from '@microsoft/signalr';
import { apiClient, AUTH_TOKEN_KEY } from '@/api/client';
import type { ChatMessageModel } from '@/types/chat';

/**
 * Singleton state pour le ChatHub SignalR (cf KSAdmin Web Hubs/ChatHub).
 * Inspire de Siclop.KSMtg.Vue/src/modules/chat/composables/useChatHub.ts.
 *
 * Methodes Hub cote serveur (mappees sur invoke) :
 *   - JoinRoom(int roomId)
 *   - LeaveRoom(int roomId)
 *   - SendMessage(int roomId, string content, int? replyToMessageId)
 *   - EditMessage(int messageId, string content)
 *   - DeleteMessage(int messageId, int roomId)
 *   - StartTyping(int roomId), StopTyping(int roomId)
 *   - MarkAsRead(int roomId)
 *   - GetOnlineUsers() -> int[]
 *
 * Events serveur -> client (subscribe via .on) :
 *   - ReceiveMessage(roomId, message)
 *   - MessageEdited(messageId, content)
 *   - MessageDeleted(messageId)
 *   - UserTyping(roomId, userId, userName), UserStoppedTyping(roomId, userId)
 *   - MessagesRead(roomId, userId)
 *   - UserOnline(userId), UserOffline(userId)
 */

const connection = ref<signalR.HubConnection | null>(null);
const isConnected = ref(false);
const onlineUserIds = ref<Set<number>>(new Set());

export function useChatHub() {

  /**
   * Recupere l'access token pour SignalR. Strategie :
   *  1) cookie HttpOnly -> GET /auth/token (KStone GetTokenFromCookie)
   *  2) fallback localStorage (jwtToken, cf api/client.ts)
   */
  async function getAccessToken(): Promise<string> {
    try {
      const base = apiClient.defaults.baseURL ?? '';
      const res = await fetch(`${base}/auth/token`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json() as { accessToken?: string; token?: string };
        if (data.accessToken) return data.accessToken;
        if (data.token) return data.token;
      }
    } catch { /* fallback localStorage */ }
    return localStorage.getItem(AUTH_TOKEN_KEY) ?? '';
  }

  /** Resout l'URL absolue du hub a partir du baseURL axios (vide en dev -> proxy Vite). */
  function resolveHubUrl(): string {
    const base = apiClient.defaults.baseURL ?? '';
    // Strip eventuel suffixe /api/ (convention KStone)
    const root = base.replace(/\/api\/?$/, '');
    return `${root}/hubs/chat`;
  }

  async function connect(): Promise<void> {
    if (connection.value?.state === signalR.HubConnectionState.Connected) return;

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(), {
        accessTokenFactory: () => getAccessToken(),
      })
      .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 30_000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    hub.onclose(() => { isConnected.value = false; });
    hub.onreconnected(() => { isConnected.value = true; });

    try {
      await hub.start();
      connection.value = hub;
      isConnected.value = true;
    } catch {
      isConnected.value = false;
    }
  }

  async function disconnect(): Promise<void> {
    if (!connection.value) return;
    try { await connection.value.stop(); } catch { /* ignore */ }
    connection.value = null;
    isConnected.value = false;
    onlineUserIds.value.clear();
  }

  // ============================================================
  // Invocations serveur -> hub
  // ============================================================

  async function joinRoom(roomId: number): Promise<void> {
    await connection.value?.invoke('JoinRoom', roomId);
  }

  async function leaveRoom(roomId: number): Promise<void> {
    await connection.value?.invoke('LeaveRoom', roomId);
  }

  async function sendMessage(roomId: number, content: string, replyToMessageId?: number | null): Promise<void> {
    await connection.value?.invoke('SendMessage', roomId, content, replyToMessageId ?? null);
  }

  async function editMessage(messageId: number, content: string): Promise<void> {
    await connection.value?.invoke('EditMessage', messageId, content);
  }

  async function deleteMessage(messageId: number, roomId: number): Promise<void> {
    await connection.value?.invoke('DeleteMessage', messageId, roomId);
  }

  async function startTyping(roomId: number): Promise<void> {
    await connection.value?.invoke('StartTyping', roomId);
  }

  async function stopTyping(roomId: number): Promise<void> {
    await connection.value?.invoke('StopTyping', roomId);
  }

  async function markAsRead(roomId: number): Promise<void> {
    await connection.value?.invoke('MarkAsRead', roomId);
  }

  async function refreshOnlineUsers(): Promise<void> {
    if (!connection.value) return;
    try {
      const list = await connection.value.invoke<number[]>('GetOnlineUsers');
      onlineUserIds.value = new Set(list);
    } catch { /* ignore */ }
  }

  function isUserOnline(userId: number): boolean {
    return onlineUserIds.value.has(userId);
  }

  // ============================================================
  // Event listeners (renvoie un unsubscribe)
  // ============================================================

  type Unsubscribe = () => void;

  function on<TArgs extends unknown[]>(
    event: string,
    handler: (...args: TArgs) => void,
  ): Unsubscribe {
    connection.value?.on(event, handler as (...args: unknown[]) => void);
    return () => connection.value?.off(event, handler as (...args: unknown[]) => void);
  }

  function onMessageReceived(cb: (roomId: number, msg: ChatMessageModel) => void): Unsubscribe {
    return on<[number, ChatMessageModel]>('ReceiveMessage', cb);
  }

  function onMessageEdited(cb: (messageId: number, content: string) => void): Unsubscribe {
    return on<[number, string]>('MessageEdited', cb);
  }

  function onMessageDeleted(cb: (messageId: number) => void): Unsubscribe {
    return on<[number]>('MessageDeleted', cb);
  }

  function onUserTyping(cb: (roomId: number, userId: number, userName: string) => void): Unsubscribe {
    return on<[number, number, string]>('UserTyping', cb);
  }

  function onUserStoppedTyping(cb: (roomId: number, userId: number) => void): Unsubscribe {
    return on<[number, number]>('UserStoppedTyping', cb);
  }

  function onMessagesRead(cb: (roomId: number, userId: number) => void): Unsubscribe {
    return on<[number, number]>('MessagesRead', cb);
  }

  function onUserOnline(cb: (userId: number) => void): Unsubscribe {
    return on<[number]>('UserOnline', (userId) => {
      onlineUserIds.value.add(userId);
      cb(userId);
    });
  }

  function onUserOffline(cb: (userId: number) => void): Unsubscribe {
    return on<[number]>('UserOffline', (userId) => {
      onlineUserIds.value.delete(userId);
      cb(userId);
    });
  }

  return {
    // state
    connection,
    isConnected,
    onlineUserIds,
    // lifecycle
    connect,
    disconnect,
    // invokes
    joinRoom,
    leaveRoom,
    sendMessage,
    editMessage,
    deleteMessage,
    startTyping,
    stopTyping,
    markAsRead,
    refreshOnlineUsers,
    isUserOnline,
    // events
    onMessageReceived,
    onMessageEdited,
    onMessageDeleted,
    onUserTyping,
    onUserStoppedTyping,
    onMessagesRead,
    onUserOnline,
    onUserOffline,
  };
}
